<template>
  <div class="cb-dashboard">
    <div class="d-flex flex-wrap justify-content-between align-items-center mb-3">
      <div>
        <h1 class="mb-1 font-regular">Customer Behavior</h1>
        <p class="text-muted mb-0">
          Growth, KYC, transactions, nano-loan uptake, and risk — existing data only
        </p>
      </div>
      <div class="d-flex flex-wrap align-items-center cb-toolbar">
        <div class="cb-range-label" v-if="rangeLabel">{{ rangeLabel }}</div>
        <div class="btn-group cb-range-btns" role="group">
          <button
            v-for="opt in rangeOptions"
            :key="opt.value"
            type="button"
            class="btn btn-sm"
            :class="range === opt.value && !customMode ? 'btn-primary' : 'btn-outline-secondary'"
            @click="setRange(opt.value)"
          >
            {{ opt.label }}
          </button>
        </div>
        <div class="d-flex align-items-center cb-custom-dates">
          <input type="date" class="form-control form-control-sm" v-model="customFrom" />
          <span class="mx-1 text-muted">→</span>
          <input type="date" class="form-control form-control-sm" v-model="customTo" />
          <button
            type="button"
            class="btn btn-sm btn-outline-primary ml-2"
            :disabled="!customFrom || !customTo"
            @click="applyCustomRange"
          >
            Apply
          </button>
        </div>
      </div>
    </div>

    <div v-if="loading" class="text-center py-5 text-muted">Loading dashboard...</div>

    <template v-else-if="dashboard">
      <!-- KPI strip -->
      <div class="row">
        <div
          class="col-xl-3 col-lg-4 col-md-6 mb-3"
          v-for="card in kpiCards"
          :key="card.key"
        >
          <div class="cb-kpi-card">
            <div class="d-flex justify-content-between align-items-start">
              <div>
                <div class="cb-kpi-label">{{ card.label }}</div>
                <div class="cb-kpi-value">{{ card.value }}</div>
                <div class="cb-kpi-sub" v-if="card.sub">{{ card.sub }}</div>
              </div>
              <span class="cb-kpi-icon">{{ card.icon }}</span>
            </div>
            <div
              class="cb-trend mt-2"
              :class="card.trend >= 0 ? 'up' : 'down'"
              v-if="card.trend !== null && card.trend !== undefined"
            >
              {{ card.trend >= 0 ? "+" : "" }}{{ card.trend }}% vs prior period
            </div>
          </div>
        </div>
      </div>

      <div class="row">
        <!-- KYC funnel -->
        <div class="col-lg-4 mb-3">
          <div class="card card--no-shadow cb-panel h-100">
            <div class="card-body">
              <h5 class="mb-1">KYC Funnel</h5>
              <p class="text-muted small mb-3">KYC records created in this period</p>
              <div
                class="cb-funnel-row"
                v-for="stage in kycFunnel"
                :key="stage.key"
              >
                <div class="d-flex justify-content-between mb-1">
                  <span>{{ stage.label }}</span>
                  <strong>{{ formatNumber(stage.count) }}</strong>
                </div>
                <div class="cb-bar-track">
                  <div
                    class="cb-bar-fill"
                    :style="{ width: funnelBarWidth(stage.count) + '%', background: '#3b82f6' }"
                  ></div>
                </div>
              </div>
              <div class="row mt-3 cb-meta-stats">
                <div class="col-6">
                  <div class="label">ID approval rate</div>
                  <div class="value">{{ dashboard.kyc.approval_rate.identity }}%</div>
                </div>
                <div class="col-6">
                  <div class="label">Bill approval rate</div>
                  <div class="value">{{ dashboard.kyc.approval_rate.utility_bill }}%</div>
                </div>
                <div class="col-6 mt-2">
                  <div class="label">AI hrs to ID</div>
                  <div class="value">
                    {{ dashboard.kyc.avg_ai_hours_to_id != null ? dashboard.kyc.avg_ai_hours_to_id + "h" : "—" }}
                  </div>
                </div>
                <div class="col-6 mt-2">
                  <div class="label">AI hrs to bill</div>
                  <div class="value">
                    {{ dashboard.kyc.avg_ai_hours_to_bill != null ? dashboard.kyc.avg_ai_hours_to_bill + "h" : "—" }}
                  </div>
                </div>
              </div>
              <p class="text-muted small mt-2 mb-0">{{ dashboard.kyc.timing_note }}</p>
            </div>
          </div>
        </div>

        <!-- Txn mix -->
        <div class="col-lg-4 mb-3">
          <div class="card card--no-shadow cb-panel h-100">
            <div class="card-body">
              <h5 class="mb-1">Transaction Mix</h5>
              <p class="text-muted small mb-3">
                {{ formatNumber(dashboard.transactions.volume.successful) }} successful ·
                {{ dashboard.transactions.volume.success_rate }}% success rate
              </p>
              <div
                class="cb-funnel-row"
                v-for="row in categoryRows"
                :key="row.category"
              >
                <div class="d-flex justify-content-between mb-1">
                  <span class="text-capitalize">{{ row.category }}</span>
                  <strong>{{ formatNumber(row.count) }}</strong>
                </div>
                <div class="cb-bar-track">
                  <div
                    class="cb-bar-fill"
                    :style="{ width: categoryBarWidth(row.count) + '%', background: '#0d9488' }"
                  ></div>
                </div>
                <div class="text-muted small">{{ formatMoney(row.amount) }}</div>
              </div>
              <div class="row mt-3 cb-meta-stats">
                <div class="col-6">
                  <div class="label">Avg ticket</div>
                  <div class="value">{{ formatMoney(dashboard.transactions.avg_ticket_size) }}</div>
                </div>
                <div class="col-6">
                  <div class="label">Txns / active user</div>
                  <div class="value">{{ dashboard.transactions.avg_txns_per_active_user }}</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Nano loan -->
        <div class="col-lg-4 mb-3">
          <div class="card card--no-shadow cb-panel h-100">
            <div class="card-body">
              <h5 class="mb-1">Nano Loan Uptake</h5>
              <p class="text-muted small mb-3">Applications opened in this period, followed through period end</p>
              <div class="cb-lifecycle">
                <div class="cb-life-pill" v-for="stage in nanoConversion" :key="stage.key">
                  <span class="name">{{ stage.label }}</span>
                  <span class="count">{{ formatNumber(stage.count) }}</span>
                </div>
              </div>
              <div class="row mt-3 cb-meta-stats">
                <div class="col-6">
                  <div class="label">Take rate</div>
                  <div class="value">{{ dashboard.nano_loans.take_rate }}%</div>
                </div>
                <div class="col-6">
                  <div class="label">Repeat rate</div>
                  <div class="value">{{ dashboard.nano_loans.repeat_rate }}%</div>
                </div>
                <div class="col-6 mt-2">
                  <div class="label">Avg loan size</div>
                  <div class="value">{{ formatMoney(dashboard.nano_loans.avg_loan_size) }}</div>
                </div>
                <div class="col-6 mt-2">
                  <div class="label">App → disburse</div>
                  <div class="value">{{ dashboard.nano_loans.conversion.app_to_disburse_rate }}%</div>
                </div>
                <div class="col-6 mt-2">
                  <div class="label">Hrs to review</div>
                  <div class="value">
                    {{ dashboard.nano_loans.avg_hours_to_review != null ? dashboard.nano_loans.avg_hours_to_review + "h" : "—" }}
                  </div>
                </div>
                <div class="col-6 mt-2">
                  <div class="label">Hrs to disburse</div>
                  <div class="value">
                    {{ dashboard.nano_loans.avg_hours_to_disburse != null ? dashboard.nano_loans.avg_hours_to_disburse + "h" : "—" }}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="row">
        <!-- Platform + hourly -->
        <div class="col-lg-4 mb-3">
          <div class="card card--no-shadow cb-panel h-100">
            <div class="card-body">
              <h5 class="mb-3">Platform &amp; Type</h5>
              <div class="mb-3" v-for="p in dashboard.transactions.by_platform" :key="'p-' + p.platform">
                <div class="d-flex justify-content-between">
                  <span class="text-capitalize">{{ p.platform }}</span>
                  <strong>{{ formatNumber(p.count) }}</strong>
                </div>
                <div class="text-muted small">{{ formatMoney(p.amount) }}</div>
              </div>
              <hr />
              <div class="mb-2" v-for="t in dashboard.transactions.by_type" :key="'t-' + t.type">
                <div class="d-flex justify-content-between">
                  <span class="text-capitalize">{{ t.type }}</span>
                  <strong>{{ formatNumber(t.count) }}</strong>
                </div>
              </div>
              <hr />
              <h6 class="mb-2">By day of week</h6>
              <div
                class="cb-funnel-row"
                v-for="d in dashboard.transactions.by_day_of_week"
                :key="d.day"
              >
                <div class="d-flex justify-content-between mb-1">
                  <span>{{ d.day }}</span>
                  <strong>{{ formatNumber(d.count) }}</strong>
                </div>
                <div class="cb-bar-track">
                  <div
                    class="cb-bar-fill"
                    :style="{ width: dowBarWidth(d.count) + '%', background: '#6366f1' }"
                  ></div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Risk aging -->
        <div class="col-lg-4 mb-3">
          <div class="card card--no-shadow cb-panel h-100">
            <div class="card-body">
              <h5 class="mb-3">Due This Period &amp; Still Overdue</h5>
              <div
                class="cb-aging-row"
                v-for="bucket in agingBuckets"
                :key="bucket.key"
              >
                <div class="d-flex justify-content-between mb-1">
                  <span>{{ bucket.label }}</span>
                  <strong>{{ formatMoney(bucket.amount) }}</strong>
                </div>
                <div class="cb-bar-track">
                  <div
                    class="cb-bar-fill"
                    :style="{ width: agingBarWidth(bucket.amount) + '%', background: bucket.color }"
                  ></div>
                </div>
                <div class="text-muted small mt-1">{{ bucket.count }} loans</div>
              </div>
              <div class="row mt-3 cb-meta-stats">
                <div class="col-4">
                  <div class="label">Still overdue</div>
                  <div class="value">{{ formatNumber(dashboard.risk.overdue_count) }}</div>
                </div>
                <div class="col-4">
                  <div class="label">30+ DPD rate</div>
                  <div class="value">{{ dashboard.risk.default_rate }}%</div>
                </div>
                <div class="col-4">
                  <div class="label">Collected</div>
                  <div class="value">{{ dashboard.risk.collection_rate }}%</div>
                </div>
                <div class="col-6 mt-2">
                  <div class="label">Hit 30+ DPD</div>
                  <div class="value">{{ formatNumber(dashboard.risk.defaulted_count) }}</div>
                </div>
                <div class="col-6 mt-2">
                  <div class="label">First-loan 30+ DPD</div>
                  <div class="value">{{ formatNumber(dashboard.risk.first_loan_defaults) }}</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Segments -->
        <div class="col-lg-4 mb-3">
          <div class="card card--no-shadow cb-panel h-100">
            <div class="card-body">
              <h5 class="mb-3">Customer Segments</h5>
              <div
                class="cb-segment-row"
                v-for="seg in dashboard.segments"
                :key="seg.key"
              >
                <div class="d-flex justify-content-between align-items-center">
                  <span>{{ seg.label }}</span>
                  <div class="text-right">
                    <strong>{{ formatNumber(seg.count) }}</strong>
                    <span class="text-muted small ml-2">{{ seg.pct }}%</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Hourly heatmap simple bars -->
      <div class="row">
        <div class="col-12 mb-3">
          <div class="card card--no-shadow cb-panel">
            <div class="card-body">
              <h5 class="mb-3">Successful txns by hour of day</h5>
              <div class="cb-hour-grid">
                <div
                  class="cb-hour-cell"
                  v-for="h in dashboard.transactions.heatmap_hourly"
                  :key="h.hour"
                  :title="h.hour + ':00 — ' + h.count"
                >
                  <div
                    class="cb-hour-bar"
                    :style="{ height: hourBarHeight(h.count) + 'px' }"
                  ></div>
                  <div class="cb-hour-label">{{ h.hour }}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>

    <div v-else class="text-center py-5 text-muted">
      Unable to load customer behavior data.
    </div>
  </div>
</template>

<script>
import ApiService from "@/services/api.service";

export default {
  name: "CustomerBehaviorDashboard",
  data() {
    return {
      loading: false,
      dashboard: null,
      range: "30d",
      customMode: false,
      customFrom: "",
      customTo: "",
      rangeOptions: [
        { label: "1D", value: "1d" },
        { label: "7D", value: "7d" },
        { label: "15D", value: "15d" },
        { label: "30D", value: "30d" },
        { label: "90D", value: "90d" }
      ]
    };
  },
  computed: {
    rangeLabel() {
      if (!this.dashboard || !this.dashboard.range) return "";
      return `${this.dashboard.range.from} → ${this.dashboard.range.to}`;
    },
    kpiCards() {
      if (!this.dashboard) return [];
      const k = this.dashboard.kpis;
      const t = k.trends || {};
      return [
        {
          key: "signups",
          label: "New Signups",
          value: this.formatNumber(k.signups),
          trend: t.signups,
          icon: "＋"
        },
        {
          key: "activated",
          label: "Signups Who Transacted",
          value: this.formatNumber(k.activated),
          sub: `${k.activation_rate}% of signups in this period`,
          trend: t.activated,
          icon: "◎"
        },
        {
          key: "txn_success_rate",
          label: "Txn Success Rate",
          value: `${k.txn_success_rate}%`,
          sub: `${this.formatNumber(k.txn_volume)} successful`,
          trend: t.txn_success_rate,
          icon: "✓"
        },
        {
          key: "txn_value",
          label: "Txn Value",
          value: this.formatMoney(k.txn_value),
          trend: t.txn_value,
          icon: "₦"
        },
        {
          key: "nano_take_rate",
          label: "Signup Loan Take Rate",
          value: `${k.nano_take_rate}%`,
          sub: `${this.formatNumber(k.signup_borrowers)} signups disbursed by period end`,
          trend: t.nano_take_rate,
          icon: "▣"
        },
        {
          key: "new_borrowers",
          label: "First-time Borrowers",
          value: this.formatNumber(k.new_borrowers),
          sub: "First disbursement in this period",
          trend: t.new_borrowers,
          icon: "➤"
        },
        {
          key: "overdue_balance",
          label: "Still Unpaid From This Period",
          value: this.formatMoney(k.overdue_balance),
          sub: `${this.formatNumber(k.overdue_count)} loans fell due and remain overdue`,
          trend: t.overdue_balance,
          icon: "⚠"
        },
        {
          key: "defaulted_count",
          label: "Hit 30+ Days Past Due",
          value: this.formatNumber(k.defaulted_count),
          sub: "Still overdue; due date was 30 days before this period",
          trend: t.defaulted_count,
          icon: "!"
        }
      ];
    },
    kycFunnel() {
      return (this.dashboard && this.dashboard.kyc && this.dashboard.kyc.funnel) || [];
    },
    categoryRows() {
      return (this.dashboard && this.dashboard.transactions && this.dashboard.transactions.by_category) || [];
    },
    nanoConversion() {
      if (!this.dashboard) return [];
      const c = this.dashboard.nano_loans.conversion;
      const l = this.dashboard.nano_loans.lifecycle;
      return [
        { key: "apps", label: "Applications", count: c.applications },
        { key: "reviewed", label: "Reviewed", count: c.reviewed },
        { key: "disbursed", label: "Disbursed", count: c.disbursed },
        { key: "active", label: "Still active", count: l.active },
        { key: "overdue", label: "Now overdue", count: l.overdue },
        { key: "completed", label: "Now completed", count: l.completed }
      ];
    },
    agingBuckets() {
      if (!this.dashboard) return [];
      const a = this.dashboard.risk.aging;
      return [
        { key: "d1_7", label: "1–7 days", count: a.d1_7.count, amount: a.d1_7.amount, color: "#dc3545" },
        { key: "d8_30", label: "8–30 days", count: a.d8_30.count, amount: a.d8_30.amount, color: "#fd7e14" },
        { key: "d31_60", label: "31–60 days", count: a.d31_60.count, amount: a.d31_60.amount, color: "#ffc107" },
        { key: "d60_plus", label: "60+ days", count: a.d60_plus.count, amount: a.d60_plus.amount, color: "#842029" }
      ];
    }
  },
  methods: {
    formatMoney(value) {
      const n = Number(value || 0);
      return `₦${n.toLocaleString("en-NG", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
    },
    formatNumber(value) {
      return Number(value || 0).toLocaleString("en-NG");
    },
    funnelBarWidth(count) {
      const max = Math.max(...this.kycFunnel.map(s => Number(s.count || 0)), 1);
      return Math.max(Math.round((Number(count || 0) / max) * 100), 4);
    },
    categoryBarWidth(count) {
      const max = Math.max(...this.categoryRows.map(s => Number(s.count || 0)), 1);
      return Math.max(Math.round((Number(count || 0) / max) * 100), 4);
    },
    dowBarWidth(count) {
      const rows = (this.dashboard && this.dashboard.transactions && this.dashboard.transactions.by_day_of_week) || [];
      const max = Math.max(...rows.map(s => Number(s.count || 0)), 1);
      return Math.max(Math.round((Number(count || 0) / max) * 100), 4);
    },
    agingBarWidth(amount) {
      if (!this.dashboard) return 0;
      const a = this.dashboard.risk.aging;
      const max = Math.max(a.d1_7.amount, a.d8_30.amount, a.d31_60.amount, a.d60_plus.amount, 1);
      return Math.max(Math.round((Number(amount || 0) / max) * 100), 4);
    },
    hourBarHeight(count) {
      const rows = (this.dashboard && this.dashboard.transactions && this.dashboard.transactions.heatmap_hourly) || [];
      const max = Math.max(...rows.map(s => Number(s.count || 0)), 1);
      return Math.max(Math.round((Number(count || 0) / max) * 64), 2);
    },
    setRange(value) {
      this.range = value;
      this.customMode = false;
      this.loadDashboard();
    },
    applyCustomRange() {
      if (!this.customFrom || !this.customTo) return;
      this.customMode = true;
      this.range = "custom";
      this.loadDashboard();
    },
    async loadDashboard() {
      this.loading = true;
      try {
        let url = "stats/customer-behavior?";
        if (this.customMode && this.customFrom && this.customTo) {
          url += `from=${this.customFrom}&to=${this.customTo}`;
        } else {
          url += `range=${this.range}`;
        }
        const response = await ApiService.get(url);
        this.dashboard = response.data.data;
      } catch (error) {
        console.log(error && error.response);
        this.dashboard = null;
      } finally {
        this.loading = false;
      }
    }
  },
  mounted() {
    this.loadDashboard();
  }
};
</script>

<style scoped>
.cb-dashboard {
  color: #1f2937;
}

.cb-toolbar {
  gap: 0.75rem;
}

.cb-range-label {
  font-size: 0.85rem;
  color: #6b7280;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 0.45rem 0.75rem;
}

.cb-custom-dates .form-control {
  width: auto;
  min-width: 140px;
}

.cb-kpi-card {
  border: 1px solid #e5e7eb;
  background: #fff;
  border-radius: 14px;
  padding: 1rem 1.1rem;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.04);
  height: 100%;
}

.cb-kpi-label {
  font-size: 0.85rem;
  color: #6b7280;
  margin-bottom: 0.35rem;
}

.cb-kpi-value {
  font-size: 1.35rem;
  font-weight: 700;
  line-height: 1.2;
}

.cb-kpi-sub {
  font-size: 0.8rem;
  color: #6b7280;
  margin-top: 0.15rem;
}

.cb-kpi-icon {
  width: 34px;
  height: 34px;
  border-radius: 10px;
  background: #f3f4f6;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.cb-trend {
  font-size: 0.78rem;
  font-weight: 600;
}

.cb-trend.up {
  color: #15803d;
}

.cb-trend.down {
  color: #b91c1c;
}

.cb-panel {
  border: 1px solid #e5e7eb;
  border-radius: 14px;
}

.cb-funnel-row,
.cb-aging-row {
  margin-bottom: 0.75rem;
}

.cb-bar-track {
  height: 8px;
  background: #eef2f7;
  border-radius: 999px;
  overflow: hidden;
}

.cb-bar-fill {
  height: 100%;
  border-radius: 999px;
}

.cb-meta-stats .label {
  font-size: 0.75rem;
  color: #6b7280;
}

.cb-meta-stats .value {
  font-weight: 700;
}

.cb-lifecycle {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.cb-life-pill {
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 0.75rem;
  align-items: center;
  border: 1px solid #e5e7eb;
  background: #f9fafb;
  border-radius: 999px;
  padding: 0.45rem 0.9rem;
}

.cb-life-pill .count {
  font-weight: 700;
}

.cb-segment-row {
  padding: 0.55rem 0;
  border-bottom: 1px solid #f3f4f6;
}

.cb-segment-row:last-child {
  border-bottom: none;
}

.cb-hour-grid {
  display: flex;
  align-items: flex-end;
  gap: 0.35rem;
  min-height: 90px;
  overflow-x: auto;
}

.cb-hour-cell {
  flex: 1;
  min-width: 28px;
  text-align: center;
}

.cb-hour-bar {
  background: #3b82f6;
  border-radius: 4px 4px 0 0;
  margin: 0 auto;
  width: 70%;
  min-height: 2px;
}

.cb-hour-label {
  font-size: 0.7rem;
  color: #6b7280;
  margin-top: 0.25rem;
}

@media (max-width: 768px) {
  .cb-custom-dates {
    width: 100%;
  }

  .cb-custom-dates .form-control {
    min-width: 0;
    flex: 1;
  }
}
</style>
